import { Injectable } from '@angular/core';
import { Action, Selector, State, type StateContext } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import * as FlatModels from '@medsurf/flat-models';
import { MenuSharedActions } from '@medsurf/flat-actions';
import * as FlatStates from '../../internal';

export interface MenuSharedStateModel {
  viewerDisplayMode: FlatModels.MenuSharedModels.ViewerDisplayMode;
  viewerPrintMode: FlatModels.MenuSharedModels.ViewerPrintMode;
  showVirtualPointer: boolean;
  showNavigator: boolean;
  showMarkers: boolean;
  showSelftest: boolean;
  showCaseHistory: boolean;
  showQuizSolution: boolean;
  showQuizExplanation: boolean;
  showQuizFeedback: boolean;
}

@State<MenuSharedStateModel>({
  name: 'menuShared',
  defaults: {
    viewerDisplayMode: FlatModels.MenuSharedModels.ViewerDisplayMode.enum.NORMAL,
    viewerPrintMode: FlatModels.MenuSharedModels.ViewerPrintMode.PRINT_USER,
    showVirtualPointer: false,
    showNavigator: true,
    showMarkers: true,
    showSelftest: false,
    showCaseHistory: false,
    showQuizExplanation: false,
    showQuizFeedback: false,
    showQuizSolution: false
  }
})
@Injectable()
export class MenuSharedState {

  //<editor-fold desc="Selectors">

  @Selector([MenuSharedState])
  static viewerDisplayMode$(state: MenuSharedStateModel): FlatModels.MenuSharedModels.ViewerDisplayMode {
    return state.viewerDisplayMode;
  }

  @Selector([MenuSharedState])
  static viewerPrintMode$(state: MenuSharedStateModel): FlatModels.MenuSharedModels.ViewerPrintMode {
    return state.viewerPrintMode;
  }

  @Selector([MenuSharedState.viewerDisplayMode$])
  static showPrint$(viewerDisplayMode: FlatModels.MenuSharedModels.ViewerDisplayMode): boolean {
    return viewerDisplayMode === FlatModels.MenuSharedModels.ViewerDisplayMode.enum.PRINT;
  }

  @Selector([MenuSharedState.viewerDisplayMode$, MenuSharedState.viewerPrintMode$])
  static showPrintForAuthor$(viewerDisplayMode: FlatModels.MenuSharedModels.ViewerDisplayMode,
                             viewerPrintMode: FlatModels.MenuSharedModels.ViewerPrintMode): boolean {
    return viewerDisplayMode === FlatModels.MenuSharedModels.ViewerDisplayMode.enum.PRINT && viewerPrintMode === FlatModels.MenuSharedModels.ViewerPrintMode.PRINT_AUTHOR;
  }

  @Selector([MenuSharedState.viewerDisplayMode$, MenuSharedState.viewerPrintMode$])
  static showPrintForUser$(viewerDisplayMode: FlatModels.MenuSharedModels.ViewerDisplayMode,
                           viewerPrintMode: FlatModels.MenuSharedModels.ViewerPrintMode): boolean {
    return viewerDisplayMode === FlatModels.MenuSharedModels.ViewerDisplayMode.enum.PRINT && viewerPrintMode === FlatModels.MenuSharedModels.ViewerPrintMode.PRINT_USER;
  }

  @Selector([MenuSharedState.viewerDisplayMode$])
  static showFullscreen$(viewerDisplayMode: FlatModels.MenuSharedModels.ViewerDisplayMode): boolean {
    return viewerDisplayMode === FlatModels.MenuSharedModels.ViewerDisplayMode.enum.FULLSCREEN;
  }

  @Selector([MenuSharedState])
  static showVirtualPointer$(state: MenuSharedStateModel): boolean {
    return state.showVirtualPointer;
  }

  @Selector([MenuSharedState])
  static showNavigator$(state: MenuSharedStateModel): boolean {
    return state.showNavigator;
  }

  @Selector([MenuSharedState])
  static showMarkers$(state: MenuSharedStateModel): boolean {
    return state.showMarkers;
  }

  @Selector([MenuSharedState])
  static showSelftest$(state: MenuSharedStateModel): boolean {
    return state.showSelftest;
  }

  @Selector([MenuSharedState])
  static showCaseHistory$(state: MenuSharedStateModel): boolean {
    return state.showCaseHistory;
  }

  @Selector([
    FlatStates.ImageEntityState.allCurrentSelectedSlideImages$,
    FlatStates.PageEntityState.currentSelectedSlidePage$,
    FlatStates.MediaEntityState.typedEntities$,
    FlatStates.StainEntityState.typedEntities$,
    FlatStates.ModalityEntityState.typedEntities$
  ])
  static currentSlideTitle$(allCurrentImages: FlatModels.ImageEntityModels.Image[],
                            currentSelectedSlidePage: FlatModels.PageEntityModels.Slide | FlatModels.PageEntityModels.Training | null,
                            typedMediaEntities: { [id: string]: FlatModels.MediaEntityModels.MediaEntityType },
                            typedStainEntities: { [id: string]: FlatModels.StainEntityModels.Stain },
                            typedModalityEntities: { [id: string]: FlatModels.ModalityEntityModels.Modality }) {
    if (!currentSelectedSlidePage) { 
      return null;
    }

    let tmp_compoundNumber: string | undefined = undefined;
    let tmp_stain: FlatModels.StainEntityModels.Stain | undefined = undefined;

    if (allCurrentImages) {
      loop1:
        for (const image of allCurrentImages) {
          const media = typedMediaEntities[image.media as unknown as string];
          if (media) {
            if (media.compoundNumber && !tmp_compoundNumber) {
              tmp_compoundNumber = media.compoundNumber;
            }
            const stain = typedStainEntities[media.stain as unknown as string];
            if (stain && !tmp_stain) {
              tmp_stain = stain;
            }
          }
          if (tmp_compoundNumber && tmp_stain) {
            break loop1;
          }
        }
    }

    if (currentSelectedSlidePage.type === FlatModels.PageEntityModels.PageType.enum.Training) {
      return 'Quiz';
    }
    let slideTitle = '';

    const modality = typedModalityEntities[currentSelectedSlidePage.modality as unknown as string];
    if (modality && modality.name) {
      slideTitle += modality.name;
    }
    if (tmp_compoundNumber) {
      slideTitle += ' ' + tmp_compoundNumber;
    }
    if (slideTitle !== '') {
      slideTitle += ': <br class="d-lg-none">';
    }
    slideTitle += currentSelectedSlidePage.title;
    if (tmp_stain?.name.length) {
      slideTitle += ' - ' + tmp_stain.name;
    }

    return slideTitle;
  }

  //</editor-fold>

  //<editor-fold desc="Actions">

  @Action(MenuSharedActions.SetViewerDisplayMode)
  setViewerDisplayMode({patchState}: StateContext<MenuSharedStateModel>,
                       {viewerDisplayMode}: MenuSharedActions.SetViewerDisplayMode) {
    patchState({
      viewerDisplayMode
    });
  }

  @Action(MenuSharedActions.SetViewerPrintMode)
  setViewerPrintMode({patchState}: StateContext<MenuSharedStateModel>,
                     {viewerPrintMode}: MenuSharedActions.SetViewerPrintMode) {
    patchState({
      viewerPrintMode
    });
  }

  @Action(MenuSharedActions.ToggleVirtualPointer)
  toggleVirtualPointer({setState}: StateContext<MenuSharedStateModel>) {
    setState(
      patch({
        showVirtualPointer: existing => !existing
      })
    );
  }

  @Action(MenuSharedActions.ToggleNavigator)
  toggleNavigator({setState}: StateContext<MenuSharedStateModel>, { override }: MenuSharedActions.ToggleNavigator) {
    setState(
      patch({
        showNavigator: existing => override ?? !existing
      })
    );
  }

  @Action(MenuSharedActions.ToggleMarker)
  toggleMarker({getState, setState}: StateContext<MenuSharedStateModel>,
               {override}: MenuSharedActions.ToggleMarker) {
    const showMarkers = override !== null ? override : !MenuSharedState.showMarkers$(getState());
    setState(
      patch({
        showMarkers,
        showSelftest: existing => (existing && showMarkers) ? false : existing,
      })
    );
  }

  @Action(MenuSharedActions.ToggleSelftest)
  toggleSelftest({getState, setState}: StateContext<MenuSharedStateModel>) {
    const showSelftest = !MenuSharedState.showSelftest$(getState());
    setState(
      patch({
        showSelftest,
        showMarkers: existing => (existing && showSelftest) ? false : existing,
      })
    );
  }

  @Action(MenuSharedActions.ToggleCaseHistory)
  toggleCaseHistory({setState}: StateContext<MenuSharedStateModel>) {
    setState(
      patch({
        showCaseHistory: existing => !existing
      })
    );
  }

  @Action(MenuSharedActions.ToggleQuizFeedback)
  toggleQuizFeedback({setState}: StateContext<MenuSharedStateModel>) {
    setState(
      patch({
        showQuizFeedback: existing => !existing
      })
    );
  }

  @Action(MenuSharedActions.ToggleQuizSolution)
  toggleQuizSolution({setState}: StateContext<MenuSharedStateModel>) {
    setState(
      patch({
        showQuizSolution: existing => !existing
      })
    );
  }

  @Action(MenuSharedActions.ToggleQuizExplanation)
  toggleQuizExplanation({setState}: StateContext<MenuSharedStateModel>) {
    setState(
      patch({
        showQuizExplanation: existing => !existing
      })
    );
  }

  //</editor-fold>
}
